import { writeThirdPartyLoginPayload } from '../utils/third-party-login'

type ThirdPartyProvider = 'google' | 'apple' | 'wechat' | 'telegram'

type TelegramAuthPayload = {
  auth_date?: number | string
  id?: number | string
  first_name?: string
  last_name?: string
  hash?: string
  photo_url?: string
}

const getThirdPartyNodeData = (response: any, routeName: string) => response?.usercenter?.[routeName]?.data || null

const isFalseLike = (value: unknown) => value === false || value === 0 || String(value).toLowerCase() === 'false'

const getThirdPartyLoginType = (payload: Record<string, any>) =>
  String(payload.loginType || payload.accountType || payload.LoginType || payload.AccountType || '')

const hasThirdPartyRegisterIdentity = (payload: Record<string, any>) =>
  Boolean(payload.id || payload.openId || payload.OpenId || payload.userId || payload.email || payload.Email)

const getRegisterReturnUrl = () => {
  if (!import.meta.client) return ''
  return window.location.href.includes('?')
    ? window.location.href.slice(0, window.location.href.indexOf('?'))
    : window.location.href
}

export const useThirdPartyLogin = () => {
  const appStore = useAppStore()

  const localePath = useLocalePath()
  const { invokeV3 } = useLoginApi()

  const thirdPartyUrls = ref<Record<string, string>>({})
  const loadingProvider = ref<ThirdPartyProvider | ''>('')
  const appleReady = ref(false)

  /**
   * 根据命名路由生成第三方登录中间页路径。
   */
  const getUserLoginPath = (query: Record<string, string> = {}) =>
    localePath({
      name: 'userlogin',
      query,
    })

  /**
   * 根据命名路由生成给第三方平台回调使用的完整中间页地址。
   */
  const getUserLoginFullUrl = (query: Record<string, string> = {}) =>
    new URL(getUserLoginPath(query), window.location.origin).href

  const buildRegisterRedirect = (payload: Record<string, any>) => {
    if (!import.meta.client) return

    const query: Record<string, string> = {
      type: 'register',
      id: String(payload.id || payload.openId || payload.OpenId || payload.userId || ''),
      name: String(payload.nickName || payload.name || payload.nick || payload.accountNick || ''),
      img: String(payload.img || payload.avatar || payload.accountHead || payload.accountHeadUrl || ''),
      logintype: getThirdPartyLoginType(payload),
      returnurl: getRegisterReturnUrl(),
    }

    // const email = payload.email || payload.Email
    // if (email) {
    //   query.email = String(email)
    // }
    if (payload._provider) {
      query.provider = String(payload._provider)
    }

    navigateTo(getUserLoginPath(query), { external: true })
  }

  /**
   * 将第三方登录成功结果统一交给 userlogin.html 继续处理。
   */
  const buildLoginRedirect = (payload: Record<string, any>) => {
    if (!import.meta.client) return

    const loginType = getThirdPartyLoginType(payload)
    const query: Record<string, string> = {
      type: 'login',
      returnurl: getRegisterReturnUrl(),
    }

    if (loginType) {
      query.logintype = loginType
    }

    if (payload.cookieValue) {
      query.cookievalue = String(payload.cookieValue)
    }

    if (payload.code) {
      query.code = String(payload.code)
    }

    if ((payload.userId || payload.LoginToken) && writeThirdPartyLoginPayload(payload)) {
      query.thirdpayload = '1'
    }

    navigateTo(getUserLoginPath(query), { external: true })
  }

  const handleThirdPartyResult = (payload: Record<string, any> | null | undefined, provider?: string) => {
    if (!payload) return

    if (provider) {
      payload._provider = provider
    }

    const registerValue = payload.isRegister ?? payload.IsRegister ?? payload.isregister
    if (isFalseLike(registerValue)) {
      buildRegisterRedirect(payload)
      return
    }

    if (payload.cookieValue || payload.userId || payload.LoginToken) {
      buildLoginRedirect(payload)
      return
    }

    if (hasThirdPartyRegisterIdentity(payload)) {
      buildRegisterRedirect(payload)
    }
  }

  const getWechatHref = () => {
    if (!import.meta.client) return ''

    const baseUrl = thirdPartyUrls.value.Wechat || ''
    if (!baseUrl) return ''

    const middlepage = getUserLoginFullUrl()
    const fromurl = appStore.allHost || window.location.href

    return `${baseUrl}&fromurl=${encodeURIComponent(fromurl)}&middlepage=${encodeURIComponent(middlepage)}`
  }

  const handleWechatLogin = () => {
    const href = getWechatHref()
    if (!href || !import.meta.client) return

    if (appStore.userlogin2) {
      window.parent?.postMessage({ op_type: 'opennew', url: href, parm: '' }, '*')
      return
    }

    window.location.href = href
  }

  const handleGoogleAuthSuccess = async (credential = '') => {
    if (!credential) return

    loadingProvider.value = 'google'
    const result = await invokeV3('usercenter$googlecallback', { Token: credential }).catch(() => null)

    handleThirdPartyResult(getThirdPartyNodeData(result, 'googlecallback'), 'google')
    loadingProvider.value = ''
  }

  const handleTelegramAuthSuccess = async (payload: TelegramAuthPayload) => {
    if (!payload?.id || !payload?.hash) return

    loadingProvider.value = 'telegram'
    const telegramPayload = {
      auth_date: payload?.auth_date,
      id: payload?.id,
      first_name: payload?.first_name,
      last_name: payload?.last_name,
      hash: payload?.hash,
      photo_url: payload?.photo_url || '',
    }
    const result = await invokeV3('usercenter$telegramcallback', telegramPayload).catch(() => null)
    handleThirdPartyResult(getThirdPartyNodeData(result, 'telegramcallback'), 'telegram')
    loadingProvider.value = ''
  }

  const initApple = () => {
    if (!import.meta.client) return

    const AppleID = window.AppleID
    if (!AppleID?.auth) return

    AppleID.auth.init({
      clientId: 'com.wikibit.www',
      scope: 'name email',
      redirectURI: window.location.origin,
      state: 'xcoiv98y2kd22vusuye3kch',
      usePopup: true,
    })
    appleReady.value = true
  }

  const loadApple = () => {
    if (!import.meta.client) return

    if (window.AppleID?.auth) {
      initApple()
      return
    }

    if (document.querySelector('script[data-wikibit-apple-login]')) return

    const script = document.createElement('script')
    script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js'
    script.async = true
    script.dataset.wikibitAppleLogin = 'true'
    script.addEventListener('load', initApple)
    document.head.appendChild(script)
  }

  const handleAppleLogin = async () => {
    if (!import.meta.client) return

    if (!appleReady.value) {
      loadApple()
      return
    }

    const AppleID = window.AppleID
    if (!AppleID?.auth) return

    try {
      loadingProvider.value = 'apple'
      const data = await AppleID.auth.signIn()
      const result = await invokeV3('usercenter$webapplecallback', {
        code: data?.authorization?.code || '',
      }).catch(() => null)
      handleThirdPartyResult(getThirdPartyNodeData(result, 'webapplecallback'), 'apple')
    } catch {
      // User closed the popup.
    } finally {
      loadingProvider.value = ''
    }
  }

  const handleSocialLogin = async (provider: ThirdPartyProvider) => {
    if (provider === 'wechat') {
      handleWechatLogin()
      return
    }

    if (provider === 'google') {
      return
    }

    if (provider === 'telegram') {
      return
    }

    await handleAppleLogin()
  }

  const initThirdPartyLogin = async () => {
    loadApple()

    const result = await invokeV3('usercenter$getbitwechatloginurl', {}).catch(() => null)
    const items = result?.usercenter?.getbitwechatloginurl?.data || []
    const nextMap: Record<string, string> = {}
    items.forEach((item: Record<string, string>) => {
      if (item.LoginType && item.LoginUrl) {
        nextMap[item.LoginType] = item.LoginUrl
      }
    })
    thirdPartyUrls.value = nextMap
  }

  return {
    handleGoogleAuthSuccess,
    handleSocialLogin,
    handleTelegramAuthSuccess,
    initThirdPartyLogin,
    loadingProvider,
  }
}
